import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Search, 
  Filter,
  Send,
  CheckCircle2,
  Clock,
  FileText,
  AlertCircle,
  MoreVertical,
  Phone,
  Mail,
  Loader2
} from 'lucide-react';
import { formatINR } from '@/utils/currency';
import BrokerSidebar from '@/components/broker/BrokerSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DashboardSkeleton, ErrorBanner } from '@/components/ui/loading-states';

const statusConfig = {
  invited: { label: 'Invited', color: 'border-gray-500 text-gray-400 bg-gray-500/10', icon: Send },
  kyc_pending: { label: 'KYC Pending', color: 'border-yellow-500 text-yellow-400 bg-yellow-500/10', icon: Clock },
  funds_received: { label: 'Funds Received', color: 'border-[#4ADE80] text-[#4ADE80] bg-[#4ADE80]/10', icon: CheckCircle2 },
  docs_signed: { label: 'Docs Signed', color: 'border-indigo-500 text-indigo-400 bg-indigo-500/10', icon: FileText },
  failed: { label: 'Failed', color: 'border-red-500 text-red-400 bg-red-500/10', icon: AlertCircle },
};

export default function BrokerInvestors() {
  const [investors, setInvestors] = useState([]);
  const [stats, setStats] = useState({ total: 0, capitalConfirmed: 0, pendingKYC: 0, awaitingResponse: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [units, setUnits] = useState([]);
  
  const [newInvestor, setNewInvestor] = useState({
    name: '',
    email: '',
    phone: '',
    amount: 500000,
    unitId: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchInvestors();
    fetchUnits();
  }, []);

  const fetchInvestors = async () => {
    setLoading(true);
    setError(null);
    try {
      let brokerId = user.brokerId;
      if (!brokerId) {
        try {
          const profileRes = await api.get(`/broker/status/${user.id}`);
          brokerId = profileRes.data.brokerId;
        } catch { /* ignore */ }
      }

      const effectiveId = brokerId || user.id;

      // Fetch investor list AND centralized metrics in parallel
      const [investorsRes, metricsRes] = await Promise.all([
        api.get(`/broker/${effectiveId}/my-investors`),
        api.get(`/metrics/broker/${effectiveId}`)
      ]);

      const investorList = (investorsRes.data.investors || []).map(inv => ({
        ...inv,
        status: inv.latestState === 'CAPITAL_PAID' || inv.latestState === 'COMPLETED' ? 'funds_received'
          : inv.latestState === 'DOC_SIGNED' ? 'docs_signed'
          : inv.latestState === 'DOC_SENT' ? 'kyc_pending'
          : inv.latestState === 'EOI_PAID' ? 'kyc_pending'
          : 'invited',
        capitalAmount: inv.totalCommitted || 0,
      }));
      setInvestors(investorList);

      // Use centralized metrics for capitalConfirmed (SSoT)
      const metricsData = metricsRes.data;
      setStats({
        total: investorList.length,
        capitalConfirmed: metricsData.capitalConfirmed || 0,
        pendingKYC: investorList.filter(i => i.status === 'kyc_pending').length,
        awaitingResponse: investorList.filter(i => i.status === 'invited').length,
      });
    } catch (err) {
      console.error('Error fetching investors:', err);
      setError('Failed to load investors. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await api.get('/units?status=OPEN');
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const handleInviteInvestor = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({
        email: newInvestor.email,
        name: newInvestor.name,
        phone: newInvestor.phone,
        amount: newInvestor.amount.toString(),
        unitId: newInvestor.unitId
      });
      
      await api.post(`/broker/${user.id}/investors/invite?${params.toString()}`);
      toast.success('Investor invited successfully!');
      setShowAddModal(false);
      setNewInvestor({ name: '', email: '', phone: '', amount: 500000, unitId: '' });
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite investor');
    }
  };

  const filteredInvestors = investors.filter(inv => {
    const matchesSearch = !searchQuery || 
      inv.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || inv.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-[#030303] flex">
      <BrokerSidebar />

      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8">
        {error && <ErrorBanner message={error} onRetry={fetchInvestors} />}
        {loading ? <DashboardSkeleton cards={4} rows={5} /> : (<>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-light tracking-tight text-white mb-1">
              My Investors
            </h1>
            <p className="text-white/50 text-sm">Manage and track your investor slots</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-medium rounded-sm"
            data-testid="add-investor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Investor
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Total Investors</p>
            <p className="text-2xl font-mono text-white">{stats.total}</p>
          </div>
          <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-sm p-4">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Capital Confirmed</p>
            <p className="text-2xl font-mono text-[#4ADE80]">{formatINR(stats.capitalConfirmed)}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Pending KYC</p>
            <p className="text-2xl font-mono text-yellow-400">{stats.pendingKYC}</p>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">Awaiting Response</p>
            <p className="text-2xl font-mono text-white">{stats.awaitingResponse}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0">
            {['all', ...Object.keys(statusConfig)].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`
                  px-4 py-2 text-sm font-mono rounded-sm whitespace-nowrap transition-all
                  ${filterStatus === status
                    ? 'bg-[#4ADE80] text-black'
                    : 'bg-[#0A0A0A] text-white/60 border border-white/10 hover:border-white/20'
                  }
                `}
              >
                {status === 'all' ? 'All' : statusConfig[status]?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Investors Table */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-white/5 text-xs text-white/40 uppercase tracking-wider">
            <div className="col-span-3">Investor</div>
            <div className="col-span-3">Deal</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Actions</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#4ADE80]" />
            </div>
          ) : filteredInvestors.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40">
                {investors.length === 0 ? 'No investors yet. Start inviting!' : 'No investors found'}
              </p>
            </div>
          ) : (
            filteredInvestors.map((investor, index) => {
              const status = statusConfig[investor.status] || statusConfig.invited;
              const StatusIcon = status.icon;

              return (
                <motion.div
                  key={investor.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border-b border-white/5 hover:bg-white/5 transition-colors"
                >
                  {/* Investor Info */}
                  <div className="col-span-3">
                    <p className="text-white font-medium mb-1">{investor.name}</p>
                    <div className="flex items-center gap-3 text-xs text-white/40">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {investor.email}
                      </span>
                    </div>
                  </div>

                  {/* Deal */}
                  <div className="col-span-3">
                    <p className="text-white/80 text-sm">{investor.dealName}</p>
                    <p className="text-xs text-white/40">Added {investor.addedDate}</p>
                  </div>

                  {/* Amount */}
                  <div className="col-span-2">
                    <p className="text-lg font-mono text-white">{formatINR(investor.amount)}</p>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-mono uppercase tracking-wider rounded-sm border ${status.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {status.label}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center gap-2">
                    {investor.status === 'invited' && (
                      <Button variant="ghost" size="sm" className="text-[#4ADE80] hover:bg-[#4ADE80]/10">
                        <Send className="w-4 h-4 mr-1" />
                        Resend
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-white/50 hover:text-white">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
        </>)}
      </main>
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#0A0A0A] border border-white/10 rounded-sm w-full max-w-md"
          >
            <div className="p-6 border-b border-white/5">
              <h2 className="text-xl font-light text-white">Add New Investor</h2>
              <p className="text-sm text-white/50 mt-1">Create a new investor slot</p>
            </div>
            <form onSubmit={handleInviteInvestor} className="p-6 space-y-4">
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider block mb-2">Full Name</label>
                <Input
                  type="text"
                  placeholder="Enter investor name"
                  value={newInvestor.name}
                  onChange={(e) => setNewInvestor({...newInvestor, name: e.target.value})}
                  className="bg-[#030303] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider block mb-2">Email</label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newInvestor.email}
                  onChange={(e) => setNewInvestor({...newInvestor, email: e.target.value})}
                  className="bg-[#030303] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider block mb-2">Phone</label>
                <Input
                  type="tel"
                  placeholder="+91 XXXXX XXXXX"
                  value={newInvestor.phone}
                  onChange={(e) => setNewInvestor({...newInvestor, phone: e.target.value})}
                  className="bg-[#030303] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider block mb-2">Investment Amount</label>
                <select 
                  value={newInvestor.amount}
                  onChange={(e) => setNewInvestor({...newInvestor, amount: parseInt(e.target.value)})}
                  className="w-full px-4 py-3 bg-[#030303] border border-white/10 rounded-sm text-white focus:outline-none focus:border-[#4ADE80]/50"
                >
                  <option value="500000">₹5,00,000 (5 Lakh)</option>
                  <option value="1000000">₹10,00,000 (10 Lakh)</option>
                  <option value="2500000">₹25,00,000 (25 Lakh)</option>
                  <option value="5000000">₹50,00,000 (50 Lakh)</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-white/40 uppercase tracking-wider block mb-2">Select Deal</label>
                <select 
                  value={newInvestor.unitId}
                  onChange={(e) => setNewInvestor({...newInvestor, unitId: e.target.value})}
                  className="w-full px-4 py-3 bg-[#030303] border border-white/10 rounded-sm text-white focus:outline-none focus:border-[#4ADE80]/50"
                  required
                >
                  <option value="">Select a property unit</option>
                  {units.map(unit => (
                    <option key={unit.unitId} value={unit.unitId}>
                      {unit.projectName || 'Property'} - Unit {unit.unitNumber || unit.unitId}
                    </option>
                  ))}
                </select>
              </div>
            
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-white/10"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Invite
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
